import styles from './PurchaseOrdersList.module.css';
import { ColumnDef } from '@tanstack/react-table';
import { ScrollView, Skeleton } from '@round/ui-kit';
import { PageTitle, assets } from '@round/whitelabel-ui';
import { VirtualizedTable } from '@round/whitelabel-ui';
import usePurchaseOrders from './usePurchaseOrders';
import { useState } from 'react';
import CreatePurchaseOrderModal from '../PurhcaseOrderModals/CreatePurchaseOrderModal/CreatePurchaseOrderModal';
import { Button } from '@round/whitelabel-ui';
import { Dropdown } from '@round/whitelabel-ui';
import UpdatePurchaseOrderModal from '../PurhcaseOrderModals/UpdatePurchaseOrderModal/UpdatePurchaseOrderModal';
import DeletePurchaseOrderModal from '../PurhcaseOrderModals/DeletePurchaseOrderModal/DeletePurchaseOrderModal';
import PurchaseOrderAllocationModal from '../PurchaseOrderAllocationModal/PurchaseOrderAllocationModal';
import { PurchaseOrderListMeta, PurchaseOrderRow, getTableMeta } from './helpers';
import POCell from './components/POCell/POCell';
import CreatedByAndFileUpload from './components/CreatedByAndFileUpload/CreatedByAndFileUpload';
import AmountCell from './components/AmountCell/AmountCell';
import InvoiceCell from './components/InvoiceCell/InvoiceCell';
import AllocationsCell from './components/AllocationsCell/AllocationsCell';
import usePurchaseOrdersListUrlState, { mapToApiParams } from './usePurchaseOrdersListUrlState';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useEffectWithPreviousDeps from '../../../../../Hooks/useEffectWithPreviousDeps';
import PurchaseOrdersListFilters from './components/PurchaseOrdersListFilters/PurchaseOrdersListFilters';
import { isEmptyObject } from 'utility/utility';

const { Edit: EditIcon, Trash: TrashIcon, Plus: PlusIcon } = assets;

const PAGE_SIZE = 10;

const PurchaseOrdersList = () => {
    const columns: ColumnDef<PurchaseOrderRow, any>[] = [
        {
            header: 'PO reference',
            id: 'number',
            accessorFn: (row) => row.number ?? '-',
            meta: {
                className: styles.poReferenceColumn,
            },
            cell: POCell,
        },
        {
            header: '',
            id: 'createdByAndFileUpload',
            meta: {
                className: styles.uploadColumn,
            },
            cell: CreatedByAndFileUpload,
        },
        {
            header: 'Amount',
            id: 'amount',
            meta: {
                className: styles.amountColumn,
            },
            cell: AmountCell,
        },
        {
            header: 'Invoice Status',
            id: 'invoiceStatus',
            meta: {
                className: styles.invoiceStatusColumn,
            },
            cell: InvoiceCell,
        },
        {
            header: 'Campaign Allocation',
            id: 'allocations',
            meta: {
                className: styles.allocationsColumn,
            },
            cell: AllocationsCell,
        },
        {
            header: '',
            id: 'actions',
            meta: {
                className: styles.actions,
            },
            cell: ({ row: { original }, table }) => {
                const { isLoading, onEditPurchaseOrder, onDeletePurchaseOrder, onAllocatePurchaseOrder } =
                    getTableMeta(table);

                if (isLoading && isEmptyObject(original)) {
                    return <Skeleton width="1rem" />;
                }

                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            icon={EditIcon}
                                            disabled={original.is_invoiced}
                                            onClick={() => {
                                                onEditPurchaseOrder(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Edit PO
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                            icon={EditIcon}
                                            onClick={() => {
                                                onAllocatePurchaseOrder(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Allocate
                                        </Dropdown.Item>

                                        <Dropdown.Separator />

                                        <Dropdown.Item
                                            disabled={original.is_invoiced}
                                            icon={() => <TrashIcon className={styles.deleteAction} />}
                                            onClick={() => {
                                                onDeletePurchaseOrder(original);
                                                setIsOpen(false);
                                            }}
                                            className={styles.deleteAction}
                                        >
                                            Delete PO
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const { selectedClientId } = useClientSelect();

    const { urlState, setUrlState } = usePurchaseOrdersListUrlState();

    useEffectWithPreviousDeps(
        (prev) => {
            const [prevClientId] = prev ?? [];
            if (prevClientId && prevClientId !== selectedClientId) {
                setUrlState({ project: undefined, search: '', team: undefined });
            }
        },
        [selectedClientId, setUrlState]
    );

    const {
        rows,
        fetchNextPage,
        hasNextPage,
        status,
        isLoading,
        isFetching,
        isFetchingNextPage,
        getIsTeamLoading,
        getIsUserLoading,
        getIsAllocationsLoading,
        getIsCampaignLoading,
        getIsBrandLoading,
        getIsProjectLoading,
        refetch,
    } = usePurchaseOrders(
        {
            page_size: PAGE_SIZE,
            ordering: '-remaining_amount',
            client_id: selectedClientId,
            ...mapToApiParams(urlState),
        },
        { refetchOnWindowFocus: false, enabled: typeof selectedClientId === 'number' }
    );

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrderRow | null>(null);

    const meta: PurchaseOrderListMeta = {
        isLoading: isFetching,
        getIsTeamLoading,
        getIsUserLoading,
        getIsAllocationsLoading,
        getIsCampaignLoading,
        getIsBrandLoading,
        getIsProjectLoading,
        refetch,
        onEditPurchaseOrder: (po) => {
            setSelectedPurchaseOrder(po);
            setIsUpdateModalOpen(true);
        },
        onDeletePurchaseOrder: (po) => {
            setSelectedPurchaseOrder(po);
            setIsDeleteModalOpen(true);
        },
        onAllocatePurchaseOrder: (po) => {
            setSelectedPurchaseOrder(po);
            setIsAllocationModalOpen(true);
        },
    };

    const isFetchingPurchaseOrders = isLoading || isFetchingNextPage;
    const emptyRows = new Array(PAGE_SIZE).fill({});
    const data = isFetchingPurchaseOrders ? rows.concat(emptyRows) : rows;

    return (
        <>
            <div className={styles.container}>
                <PageTitle>Purchase Orders</PageTitle>

                <div className={styles.row}>
                    <PurchaseOrdersListFilters
                        filters={urlState}
                        onFiltersChange={setUrlState}
                        className={styles.filters}
                    />

                    <Button appearance="primary" type="button" onClick={() => setIsCreateModalOpen(true)}>
                        <PlusIcon />
                        PO
                    </Button>
                </div>

                <ScrollView className={styles.main}>
                    <VirtualizedTable
                        isLoading={isFetchingPurchaseOrders}
                        containerClassName={styles.tableContainer}
                        meta={meta}
                        columns={columns}
                        data={data}
                        onEndReached={() => {
                            if (status === 'success' && hasNextPage) {
                                fetchNextPage();
                            }
                        }}
                        virtualizerOptions={{
                            count: data.length,
                            estimateSize: () => 50,
                            getItemKey: (index) => rows[index]?.id || index,
                            overscan: 5,
                        }}
                    />
                </ScrollView>
            </div>

            <CreatePurchaseOrderModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
            <UpdatePurchaseOrderModal
                isOpen={isUpdateModalOpen}
                onClose={() => {
                    setIsUpdateModalOpen(false);
                    setSelectedPurchaseOrder(null);
                }}
                purchaseOrder={selectedPurchaseOrder}
            />

            <DeletePurchaseOrderModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedPurchaseOrder(null);
                }}
                purchaseOrder={selectedPurchaseOrder}
            />

            <PurchaseOrderAllocationModal
                isOpen={isAllocationModalOpen}
                purchaseOrderId={selectedPurchaseOrder?.id}
                onClose={(haveAllocationsBeenWritten) => {
                    if (haveAllocationsBeenWritten) {
                        refetch();
                    }
                    setIsAllocationModalOpen(false);
                    setSelectedPurchaseOrder(null);
                }}
            />
        </>
    );
};

export default PurchaseOrdersList;
